import { useCallback, useState } from 'react';
import { studentsAPI } from '../services/api';
import { executeAdminUserFormUpdate } from '../utils/submitAdminUserFormUpdate';

function validateForm(formData) {
  if (!String(formData.firstName || '').trim()) return 'First name is required';
  if (!String(formData.lastName || '').trim()) return 'Last name is required';
  if (!String(formData.email || '').trim()) return 'Email is required';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(String(formData.email || '').trim())) return 'Enter a valid email address';
  if (!String(formData.veStudentId || '').trim()) return 'Student ID is required';
  return null;
}

/**
 * Inline edit state + full save handler for filter student rows.
 */
export function useStudentInlineUpdate({ onStudentsPatched }) {
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const startEdit = useCallback((studentId) => {
    setEditingStudentId(studentId);
    setSaveError(null);
    setSaveSuccess(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingStudentId(null);
    setSaveError(null);
  }, []);

  const saveStudent = useCallback(
    async (student, formData, helpers = {}) => {
      const validationError = validateForm(formData);
      if (validationError) {
        setSaveError(validationError);
        return false;
      }

      const studentId = student.id || student._id;
      const userId = helpers.userId;
      if (!userId) {
        setSaveError('Linked user account not found');
        return false;
      }

      setSavingStudentId(studentId);
      setSaveError(null);
      setSaveSuccess(null);

      try {
        await executeAdminUserFormUpdate({
          userId,
          formData,
          companyOptions: helpers.companyOptions,
          refreshCompanyOptions: helpers.refreshCompanyOptions,
        });

        const refreshed = await studentsAPI.getById(studentId);
        onStudentsPatched?.(studentId, refreshed.data);
        setEditingStudentId(null);
        setSaveSuccess(`Updated ${refreshed.data?.fullName || studentId}`);
        return true;
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          (Array.isArray(error.response?.data?.errors)
            ? error.response.data.errors.map((e) => e.message).join(', ')
            : null) ||
          error.message ||
          'Failed to update student';
        setSaveError(msg);
        return false;
      } finally {
        setSavingStudentId(null);
      }
    },
    [onStudentsPatched]
  );

  return {
    editingStudentId,
    savingStudentId,
    saveError,
    saveSuccess,
    setSaveSuccess,
    startEdit,
    cancelEdit,
    saveStudent,
  };
}
